import React, { useState, useEffect, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Toast from "react-bootstrap/Toast";
import InputGroup from "react-bootstrap/InputGroup";
import axios from "axios";
import moment from "moment";
import { useHistory } from "react-router-dom";
import {
    Calendar as CalendarIcon,
    ShieldLock,
    TelephoneFill
} from "react-bootstrap-icons";
import { Header, Footer } from "./components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./styles/OHHIdentityVerification.scss";
import { OHH_PATH } from "./constants";

const OHHIdentityVerification = () => {
    const history = useHistory();
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    const dobControlRef = useRef(null);

    const [formData, setFormData] = useState({
        dob: null
    });

    const [dobText, setDobText] = useState("");

    const [errors, setErrors] = useState({
        dob: ""
    });

    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccessful, setSubmitSuccessful] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [
        requiresFinancialProfessional,
        setRequiresFinancialProfessional
    ] = useState(false);
    const [
        showAlreadyRegisteredOverlay,
        setShowAlreadyRegisteredOverlay
    ] = useState(false);
    const [loginRedirectUrl, setLoginRedirectUrl] = useState("");
    const redirectTimerRef = useRef(null);

    useEffect(() => {
        document.title = "Identity Verification | Pacific Life";
    }, []);

    useEffect(() => {
        return () => {
            if (redirectTimerRef.current) {
                clearTimeout(redirectTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isDatePickerOpen) {
            return undefined;
        }

        const closeDatePickerWhenFocusLeaves = (e) => {
            if (!dobControlRef.current?.contains(e.target)) {
                setIsDatePickerOpen(false);
            }
        };

        document.addEventListener("focusin", closeDatePickerWhenFocusLeaves);
        document.addEventListener("mousedown", closeDatePickerWhenFocusLeaves);

        return () => {
            document.removeEventListener(
                "focusin",
                closeDatePickerWhenFocusLeaves
            );
            document.removeEventListener(
                "mousedown",
                closeDatePickerWhenFocusLeaves
            );
        };
    }, [isDatePickerOpen]);

    useEffect(() => {
        if (submitSuccessful) {
            setSubmitDisabled(true);
            return;
        }

        const dobValidationError = getDobErrorMessage(dobText, formData.dob);
        const hasValues = formData.dob;
        const hasNoErrors = !dobValidationError && !errors.dob;
        setSubmitDisabled(!(hasValues && hasNoErrors));
    }, [formData, errors, dobText, submitSuccessful]);

    const handleDobChange = (date) => {
        setFormData((prev) => ({ ...prev, dob: date }));
        setIsDatePickerOpen(false);
        if (date) {
            setDobText(formatDateToText(date));
        }
    };

    const handleDobTextChange = (e) => {
        const raw = e.target.value;

        // Strip everything except digits
        const digits = raw.replace(/\D/g, "").slice(0, 8);
        const slashCount = (raw.match(/\//g) || []).length;
        const hasTrailingSlash = raw.endsWith("/");

        // Re-build with slashes: MM/DD/YYYY
        let formatted = digits;
        if (digits.length > 4) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(
                2,
                4
            )}/${digits.slice(4)}`;
        } else if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        } else if (digits.length === 2 && hasTrailingSlash) {
            // Allow typing MM/ manually.
            formatted = `${digits}/`;
        }

        if (digits.length === 4 && slashCount > 1 && hasTrailingSlash) {
            // Allow typing MM/DD/ manually.
            formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/`;
        }

        setDobText(formatted);

        const parsedDate = parseDateFromText(formatted);
        if (parsedDate) {
            setFormData((prev) => ({ ...prev, dob: parsedDate }));
        } else if (formatted.length === 0) {
            setFormData((prev) => ({ ...prev, dob: null }));
        }
    };

    const handleCalendarIconClick = () => {
        setIsDatePickerOpen(true);
    };

    const handleDobTextBlur = (e) => {
        handleBlur(e);
    };

    const parseDateFromText = (text) => {
        if (!text || text.trim().length === 0) {
            return null;
        }

        const trimmedText = text.trim();
        let month, day, year;

        // Try parsing MM/DD/YYYY format (01/01/2026)
        const slashRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        const slashMatch = trimmedText.match(slashRegex);
        if (slashMatch) {
            month = parseInt(slashMatch[1], 10);
            day = parseInt(slashMatch[2], 10);
            year = parseInt(slashMatch[3], 10);
        }

        // Try parsing MMDDYYYY format (01012026)
        if (!month) {
            const noSlashRegex = /^(\d{2})(\d{2})(\d{4})$/;
            const noSlashMatch = trimmedText.match(noSlashRegex);
            if (noSlashMatch) {
                month = parseInt(noSlashMatch[1], 10);
                day = parseInt(noSlashMatch[2], 10);
                year = parseInt(noSlashMatch[3], 10);
            }
        }

        if (!month) {
            return null;
        }

        // Validate month is 1-12
        if (month < 1 || month > 12) {
            return null;
        }

        // Validate day is 1-31
        if (day < 1 || day > 31) {
            return null;
        }

        // Create the date and verify it matches the input
        const parsedDate = new Date(year, month - 1, day);

        // Verify the date is valid by checking if the components match
        // This catches invalid dates like Feb 30, which JavaScript would roll over
        if (
            isNaN(parsedDate.getTime()) ||
            parsedDate.getMonth() !== month - 1 ||
            parsedDate.getDate() !== day ||
            parsedDate.getFullYear() !== year
        ) {
            return null;
        }

        return parsedDate;
    };

    const isDateInFuture = (date) => {
        const dateToValidate = new Date(date);
        dateToValidate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return dateToValidate > today;
    };

    const getDobErrorMessage = (rawDobText, parsedDob = null) => {
        if (!rawDobText || rawDobText.trim().length === 0) {
            return "Date of Birth must be entered";
        }

        const parsedDate = parsedDob || parseDateFromText(rawDobText);
        if (!parsedDate) {
            return "Enter valid date of birth (MM/DD/YYYY or MMDDYYYY)";
        }

        if (isDateInFuture(parsedDate)) {
            return "Date of Birth cannot be in the future";
        }

        return "";
    };

    const formatDateToText = (date) => {
        if (!date) return "";
        return moment(date).format("MM/DD/YYYY");
    };

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";

        if (name === "dob-text") {
            errorMessage = getDobErrorMessage(value);
            setErrors((prev) => ({ ...prev, dob: errorMessage }));
        }
    };

    const formatPayload = () => {
        return {
            token,
            dateOfBirth: formData.dob
                ? moment(formData.dob).format("YYYY-MM-DD")
                : ""
        };
    };

    const formatOHHEnrollPayload = (data) => {
        return [
            { key: "tenant", value: "OHH" },
            { key: "firstname", value: data?.firstName || "" },
            { key: "lastname", value: data?.lastName || "" },
            { key: "middlename", value: data?.middleName || "" },
            { key: "suffix", value: data?.suffix || "" },
            { key: "dob", value: data?.dateOfBirth || "" },
            { key: "email", value: data?.email || "" },
            { key: "policynumber", value: data?.policyNumber || "" },
            { key: "ssn", value: data?.ssn || "" },
            { key: "invitationCode", value: token || "" },
            { key: "roles", value: data?.roles || [] }
        ];
    };

    const getErrorCode = (responseData) =>
        responseData?.error?.code || responseData?.code;

    const handleVerificationError = (status, responseData) => {
        const errorCode = getErrorCode(responseData);

        if (errorCode === "INVALID_DOB") {
            setToastMessage(
                "Date of birth could not be verified. Please try again"
            );
            return;
        }

        if (status === 401 && errorCode === "CONTACT_FP") {
            setRequiresFinancialProfessional(true);
            return;
        }

        setToastMessage(
            responseData?.error?.message ||
                "Something went wrong. Please try again later."
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitSuccessful) {
            return;
        }

        if (!token) {
            setToastMessage("Token not found.");
            return;
        }

        const dobError = getDobErrorMessage(dobText, formData.dob);

        if (dobError) {
            setErrors((prev) => ({
                ...prev,
                dob: dobError
            }));
            return;
        }

        setSubmitting(true);
        let errMsg = "";
        const requestPayload = formatPayload();

        try {
            const res = await axios({
                method: "POST",
                url: "/life/enroll/api/userenroll/ohh/id/verification",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(requestPayload)
            }).catch(function (err) {
                if (err.response) {
                    errMsg = err.response?.data?.error?.message;
                    handleVerificationError(
                        err.response.status,
                        err.response.data
                    );
                } else if (err.request) {
                    console.log(err.request);
                    setToastMessage(
                        "Something went wrong. Please try again later."
                    );
                } else {
                    console.log("Error", err.message);
                }
            });

            if (res && res.status === 200) {
                if (res.data?.redirect) {
                    // already-registered users are sent to login instead of enrollment
                    setSubmitSuccessful(true);
                    setLoginRedirectUrl(res.data?.data?.redirectUrl);
                    setShowAlreadyRegisteredOverlay(true);
                    redirectTimerRef.current = setTimeout(() => {
                        window.location.href = res.data?.data?.redirectUrl;
                    }, 5000);
                    return;
                }

                if (res.data?.success && res.data?.data) {
                    setSubmitSuccessful(true);
                    const payload = formatOHHEnrollPayload(res.data.data);
                    history.push(OHH_PATH, { payload });
                    return;
                } else {
                    handleVerificationError(res.status, res.data);
                }
            }
        } catch (error) {
            handleVerificationError(
                error.response?.status,
                error.response?.data
            );
            if (!error.response) {
                setToastMessage(
                    errMsg ||
                        error.response?.data?.errorMessage ||
                        "Something went wrong. Please try again later."
                );
            }
        }

        setSubmitting(false);
    };

    const closeToast = () => {
        setToastMessage("");
    };

    const submitButtonLabel = submitting ? "Submitting..." : "Submit";

    return (
        <>
            {toastMessage !== "" && (
                <Toast onClose={closeToast} className="toast-cont">
                    <Toast.Header>
                        <strong className="me-auto">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: toastMessage
                                }}
                            ></div>
                        </strong>
                    </Toast.Header>
                </Toast>
            )}
            <Header />
            <div
                className={
                    requiresFinancialProfessional
                        ? "mainMyAccount identity-locked-page"
                        : "mainMyAccount"
                }
                style={{ flex: 1 }}
            >
                {requiresFinancialProfessional ? (
                    <div className="identity-locked">
                        <ShieldLock className="identity-locked-icon" />
                        <h2 className="identity-locked-heading">
                            We couldn&apos;t verify your identity
                        </h2>
                        <p className="identity-locked-subtext">
                            Identity verification has been unsuccessful after 3
                            attempts.
                        </p>
                        <p className="identity-locked-body">
                            To protect your personal information, online
                            verification has been locked and we are unable to
                            continue electronically at this time.
                        </p>
                        <div className="identity-locked-contact-card">
                            <span className="identity-locked-contact-icon-badge">
                                <TelephoneFill className="identity-locked-contact-icon" />
                            </span>
                            <div>
                                <p>
                                    A Pacific Life representative can help
                                    complete the verification process and
                                    discuss your available options.
                                </p>
                                <p className="identity-locked-call-label">
                                    Please call:
                                </p>
                                <p className="identity-locked-phone">
                                    (800) 800-7681
                                </p>
                                <p className="identity-locked-hours">
                                    Monday &ndash; Friday | 7:00 AM &ndash; 7:00
                                    PM CT
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <h1 className="heading">(FPO) Identity Verification</h1>
                        <br />
                        <p>
                            (FPO copy) Thank you for visiting Pacific Life.
                            Please enter your date
                            <br />
                            of birth below:
                        </p>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Label>Your Date of Birth</Form.Label>
                                    <div
                                        ref={dobControlRef}
                                        className="dob-input-wrapper"
                                    >
                                        <InputGroup>
                                            <Form.Control
                                                name="dob-text"
                                                type="text"
                                                placeholder="MM/DD/YYYY or MMDDYYYY"
                                                value={dobText}
                                                onChange={handleDobTextChange}
                                                onBlur={handleDobTextBlur}
                                                maxLength="10"
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={
                                                    handleCalendarIconClick
                                                }
                                                className="calendar-icon-btn"
                                                title="Open date picker"
                                            >
                                                <CalendarIcon size={18} />
                                            </Button>
                                        </InputGroup>
                                        {isDatePickerOpen && (
                                            <DatePicker
                                                selected={formData.dob}
                                                onChange={handleDobChange}
                                                open
                                                onCalendarClose={() =>
                                                    setIsDatePickerOpen(false)
                                                }
                                                onClickOutside={() =>
                                                    setIsDatePickerOpen(false)
                                                }
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                maxDate={new Date()}
                                                wrapperClassName="datepicker-wrapper"
                                                popperClassName="ohh-datepicker-popper"
                                            />
                                        )}
                                    </div>
                                    <span className="validation-error-message">
                                        <br />
                                        {errors.dob}
                                    </span>
                                </Col>
                            </Row>
                            <div className="buttons-myaccount">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    onClick={handleSubmit}
                                    disabled={
                                        submitDisabled ||
                                        submitting ||
                                        submitSuccessful
                                    }
                                >
                                    {submitButtonLabel}
                                </Button>
                            </div>
                        </Form>
                    </>
                )}
            </div>
            <Footer />
            {showAlreadyRegisteredOverlay && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 2000,
                        backgroundColor: "rgba(0, 0, 0, 0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px"
                    }}
                >
                    <div
                        style={{
                            maxWidth: "760px",
                            width: "100%",
                            background: "#ffffff",
                            borderRadius: "12px",
                            padding: "28px 24px",
                            textAlign: "center",
                            boxShadow: "0 14px 40px rgba(0, 0, 0, 0.3)",
                            color: "#1f2a44",
                            fontSize: "18px",
                            lineHeight: "1.5",
                            fontWeight: 600
                        }}
                    >
                        <div>
                            You already have an account. You will now be
                            redirected to the login page.
                        </div>
                        <div
                            style={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#666",
                                marginTop: "20px",
                                paddingTop: "20px",
                                borderTop: "1px solid #ddd"
                            }}
                        >
                            If you're not redirected in 5 seconds,{" "}
                            <a
                                href={loginRedirectUrl}
                                style={{
                                    color: "#007bff",
                                    textDecoration: "none",
                                    fontWeight: 500
                                }}
                            >
                                click here
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OHHIdentityVerification;
